// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — links that arrive in the address fragment, never sent to a server
 * or in a Referer, and taken out of the address at once:
 *
 * - **A share link** (`#ayni-share=<share id>.<secret>`; contract section
 *   16). Kept in this tab's session storage until the person is signed in
 *   (a share link works only for a signed-in account), then **shown before
 *   it is accepted** (review L-3): the dataset's title, who shared it (their
 *   display name, review I-5) and until when, with **Open it** and **Not
 *   now**. Nothing is accepted, and the sharer learns nothing, unless the
 *   person chooses to open it; the same for a link pasted into the address
 *   later (hashchange). Accepting is `POST /api/data/shares/accept`; My
 *   data then lists the dataset under "Shared with me".
 * - **"This wasn't me"** (`#ayni-passkey-revoke=<passkey id>.<secret>`, the
 *   link mailed when a passkey is added; review M-2). The page asks first,
 *   then removes that passkey and signs the account out everywhere
 *   (`POST /api/data/passkeys/revoke`); no sign-in needed.
 */

import { t } from '../i18n'
import { formatDate } from '../i18n/format'
import { accountState, onAccountChange } from '../services/account'
import { acceptShareLink, previewShareLink, revokePasskeyByLink } from '../services/ownDataApi'
import { PASSKEY_REVOKE_PARAM, SHARE_LINK_PARAM, parseShareLink } from '../services/ownDataSharing'
import { button, h } from './myDataDom'
import { dialog } from './xrPlansUI'

const PENDING_KEY = 'ayni-share-pending'

export interface ShareLinkHooks {
  announce: (message: string) => void
  /** Open Browse on the My data tab. */
  openMyData: () => void
  /** Ask the person to sign in. */
  openSignIn: () => void
}

function session(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/** The share link in an address's fragment, or null; `cleanHref` is the address without it. */
export function shareLinkInHref(href: string): { link: string; cleanHref: string } | null {
  const u = new URL(href)
  if (!u.hash.startsWith(`#${SHARE_LINK_PARAM}=`)) return null
  const value = u.hash.slice(1)
  if (!parseShareLink(value)) return null
  u.hash = ''
  return { link: value, cleanHref: u.pathname + u.search }
}

/** The "this wasn't me" link in an address's fragment, or null. */
export function revokeLinkInHref(href: string): { link: string; cleanHref: string } | null {
  const u = new URL(href)
  if (!u.hash.startsWith(`#${PASSKEY_REVOKE_PARAM}=`)) return null
  const value = u.hash.slice(1)
  if (!/^ayni-passkey-revoke=pk[0-9a-f]{24}\.[A-Za-z0-9_-]{43}$/.test(value)) return null
  u.hash = ''
  return { link: value, cleanHref: u.pathname + u.search }
}

const ERROR_KEYS = { link_ended: 'myData.shared.linkEnded', not_found: 'myData.shared.linkWrong', bad_link: 'myData.shared.linkWrong', own_lab: 'myData.shared.ownLab' } as const

export function initShareLinkIntake(hooks: ShareLinkHooks): void {
  let busy = false
  let prompted = false
  const take = (): void => {
    const revoke = revokeLinkInHref(window.location.href)
    if (revoke) {
      window.history.replaceState(window.history.state, '', revoke.cleanHref)
      askRevoke(revoke.link)
      return
    }
    const found = shareLinkInHref(window.location.href)
    if (!found) return
    window.history.replaceState(window.history.state, '', found.cleanHref)
    try {
      session()?.setItem(PENDING_KEY, found.link)
    } catch {
      // private mode: asked about now or never
    }
    void tryAsk(found.link)
  }
  const forget = (): void => {
    try {
      session()?.removeItem(PENDING_KEY)
    } catch {
      // nothing kept
    }
  }
  const tryAsk = async (link: string | null): Promise<void> => {
    const pending = link ?? session()?.getItem(PENDING_KEY) ?? null
    if (!pending || busy) return
    const st = accountState().status
    if (st === 'unknown') return
    if (st !== 'signedIn') {
      if (prompted) return
      prompted = true
      hooks.announce(t('myData.shared.signInFirst'))
      hooks.openSignIn()
      return
    }
    busy = true
    const preview = await previewShareLink(pending)
    busy = false
    forget()
    if (!preview.ok) {
      hooks.announce(t(ERROR_KEYS[preview.error as keyof typeof ERROR_KEYS] ?? 'myData.shared.linkWrong'))
      return
    }
    const v = preview.value
    const d = dialog(t('myData.shared.askTitle'), null, 'xr-share-ask')
    if (v.ownLab) {
      d.body.append(h('p', { class: 'xr-plans-lead' }, t('myData.shared.ownLab')), button('xr-plan-cta share-ask-ok', t('myData.shared.ok')))
      d.body.querySelector('.share-ask-ok')?.addEventListener('click', () => d.close())
      return
    }
    const status = h('p', { class: 'xr-request-status', role: 'status' })
    const open = button('xr-plan-cta share-ask-open', t('myData.shared.openIt'))
    const later = button('xr-plan-cta xr-plan-cta-quiet share-ask-later', t('myData.shared.notNow'))
    d.body.append(
      h('p', { class: 'xr-plans-lead share-ask-title' }, v.dataset.title),
      h('p', null, t('myData.shared.askBody', { who: v.sharedBy ?? t('myData.shared.someoneUser'), date: formatDate(new Date(v.expiresAt), { dateStyle: 'medium' }) })),
      h('p', { class: 'mydata-hint' }, t('myData.shared.askNote')),
      status,
      h('div', { class: 'mydata-actions' }, open, later))
    later.addEventListener('click', () => d.close())
    open.addEventListener('click', () => {
      open.disabled = true
      void acceptShareLink(pending).then((reply) => {
        if (reply.ok) {
          d.close()
          hooks.announce(t('myData.shared.accepted', { title: reply.value.dataset.title }))
          hooks.openMyData()
          return
        }
        open.disabled = false
        status.textContent = t(ERROR_KEYS[reply.error as keyof typeof ERROR_KEYS] ?? 'myData.shared.linkWrong')
      })
    })
  }
  take()
  window.addEventListener('hashchange', take)
  onAccountChange((s) => {
    if (s.status !== 'unknown') void tryAsk(null)
  })
}

/** "This wasn't me": ask, then remove that passkey and sign the account out everywhere. */
function askRevoke(link: string): void {
  const d = dialog(t('passkeys.revoke.title'), null, 'xr-passkey-revoke')
  const status = h('p', { class: 'xr-request-status', role: 'status' })
  const go = button('xr-plan-cta xr-danger passkey-revoke-go', t('passkeys.revoke.go'))
  const keep = button('xr-plan-cta xr-plan-cta-quiet passkey-revoke-keep', t('passkeys.revoke.keep'))
  d.body.append(h('p', { class: 'xr-plans-lead' }, t('passkeys.revoke.body')), status, h('div', { class: 'mydata-actions' }, go, keep))
  keep.addEventListener('click', () => d.close())
  go.addEventListener('click', () => {
    go.disabled = true
    void revokePasskeyByLink(link).then((r) => {
      if (r.ok) {
        d.body.replaceChildren(h('p', { class: 'xr-plans-lead passkey-revoke-done' }, t('passkeys.revoke.done')), button('xr-plan-cta passkey-revoke-signin', t('passkeys.revoke.signIn')))
        // Every session of the account was ended: this page starts again from the welcome page.
        d.body.querySelector('.passkey-revoke-signin')?.addEventListener('click', () => window.location.assign('/'))
        return
      }
      go.disabled = false
      status.textContent = t(r.error === 'link_ended' ? 'passkeys.revoke.ended' : 'passkeys.revoke.wrong')
    })
  })
}
